import React, { useEffect, useRef, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  CartesianGrid,
  Cell,
} from "recharts";
import { getGrowthOpportunities, getTenantById } from "../../../../../shared/services/apis";
import IframeViewer from "../../../../../shared/ui/urlOpener/IFrameViewer";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import saveAs from "file-saver";
import { RiDownloadLine } from "react-icons/ri";

// ------------------- Interfaces ------------------- //

export interface GrowthOpportunitiesResponse {
  citations: string[];
  search_results: SearchResult[];
  content: GrowthOpportunityDetails;
}

export interface SearchResult {
  url: string;
  date: string;
  title: string;
  last_updated: string;
}

export interface GrowthOpportunityDetails {
  company: string;
  website: string;
  core_market: string;
  market_size_current: MarketSizeData;
  market_projection_2030_2035: MarketProjectionData;
  cagr: CAGRData;
  regional_breakdown: RegionalBreakdown[];
  key_trends_and_drivers: string[];
  growth_opportunities: string[];
}

export interface MarketSizeData {
  value_usd_billion: number;
  year: number;
  sources: string[];
}

export interface MarketProjectionData {
  value_usd_billion: number;
  year: string;
  sources: string[];
}

export interface CAGRData {
  value_percent: number;
  period: string;
  sources: string[];
}

export interface RegionalBreakdown {
  region: string;
  current_value_usd_billion: number;
  projected_value_usd_billion: number;
  cagr_percent: number;
}

// ------------------- Chart Colors ------------------- //

const colors2033 = ["#FCA5A5", "#FCD34D", "#93C5FD", "#D9F99D", "#6EE7B7"];
const colors2025 = ["#EF4444", "#F59E0B", "#3B82F6", "#4D7C0F", "#047857"];

// ------------------- Props ------------------- //

interface Props {
  tenantId: string;
  tenantServiceId: string;
  tenantServiceOptionId: string;
}

interface Tenant {
  id: string;
  name: string;
  logoUrl?: string;
}

// ------------------- Component ------------------- //

const GrowthOpportunities: React.FC<Props> = ({
  tenantId,
  tenantServiceId,
  tenantServiceOptionId,
}) => {
  const [chartData, setChartData] = useState<
    {
      region: string;
      value2025: number;
      total2033: number;
      growth: number;
    }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<GrowthOpportunitiesResponse | null>(null);
  const [startYear, setStartYear] = useState<number | string>("2025");
  const [endYear, setEndYear] = useState<number | string>("2033");
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingWord, setDownloadingWord] = useState(false);
  const [tenant, setTenant] = useState<Tenant | null>();

  
    useEffect(() => {
      if (!tenantId) return;
  
      const fetchTenant = async () => {
        try {
          const data = await getTenantById(tenantId);
          setTenant(data);
        } catch (error) {
          console.error("Error fetching tenant:", error);
        }
      };
  
      fetchTenant();
    }, [tenantId]);

  useEffect(() => {
    if (!tenantId || !tenantServiceId || !tenantServiceOptionId) return;

    const fetchData = async () => {
      try {
        const response: GrowthOpportunitiesResponse =
          await getGrowthOpportunities(
            tenantId,
            tenantServiceId,
            tenantServiceOptionId
          )
          console.log("Growth Opportunities Response:", response);
        setData(response);

        ///// dynamic years
        const start =
          response?.content?.market_size_current?.year || "2025";
        const end =
          response?.content?.market_projection_2030_2035?.year || "2033";

        setStartYear(start);
        setEndYear(end);

        const regionalData = response?.content?.regional_breakdown || [];

        const transformed = regionalData.map(
          (regionItem: RegionalBreakdown) => ({
            region: regionItem.region,
            value2025: regionItem.current_value_usd_billion,
            total2033: regionItem.projected_value_usd_billion,
            growth:
              regionItem.projected_value_usd_billion -
              regionItem.current_value_usd_billion,
          })
        );
// console.log({
//           transformed,  
//           regionalData ,
//           start,
//           end
//         });

        setChartData(transformed);
      } catch (err) {
        console.error("Failed to fetch chart data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tenantId, tenantServiceId, tenantServiceOptionId]);

  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    setIframeUrl(url); // open iframe on right side
  };

  const currentEstimateValue = () => {
    const sum = chartData.reduce(
      (acc, current) => acc + (current?.value2025 || 0),
      0
    );
    return sum;
  };
  const projectedValue = () => {
    const sum = chartData.reduce(
      (acc, current) => acc + (current?.total2033 || 0),
      0
    );
    return sum;
  };

  const calculateCAGR = (start: number, end: number, years: number): number => {
    if (start <= 0 || end <= 0 || years <= 0) return 0;
    return (Math.pow(end / start, 1 / years) - 1) * 100;
  };

  const cagrValue = calculateCAGR(currentEstimateValue(), projectedValue(), 8);

  const downloadPDF = async () => {
    setDownloadingPdf(true);
    console.log("Starting PDF generation");

    try {
      if (!chartRef.current) {
        console.error("Chart reference is null");
        alert("Chart reference is null");
        return;
      }
       // Load the watermark image with proper typing
    const loadImage = (url: string): Promise<HTMLImageElement> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
      });
    };

    // Load watermark image if logoUrl exists
    let watermarkImg: HTMLImageElement | null = null;
    if (tenant?.logoUrl) {
      try {
        watermarkImg = await loadImage(tenant.logoUrl);
      } catch (error) {
        console.warn('Failed to load watermark image:', error);
      }
    }

    // Function to add watermark to a page
    const addWatermark = (pageNumber: number) => {
      if (!watermarkImg) return;
      
      pdf.setPage(pageNumber);
      
      // Save graphics state and set transparency
      pdf.saveGraphicsState();
      pdf.setGState(pdf.GState({ opacity: 0.1 })); // Remove 'new' keyword
      
      // Calculate watermark size and position (centered)
      const watermarkWidth = 80;
      const watermarkHeight = (watermarkImg.height / watermarkImg.width) * watermarkWidth;
      const xPos = (pageWidth - watermarkWidth) / 2;
      const yPos = (pageHeight - watermarkHeight) / 2;
      
      // Add watermark image
      pdf.addImage(
        watermarkImg,
        'PNG',
        xPos,
        yPos,
        watermarkWidth,
        watermarkHeight
      );
      
      // Restore graphics state
      pdf.restoreGraphicsState();
    };

      const pageWidthPx = 750; // jsPDF default A4 size in pixels
      const pageHeightPx = 842;
      // Use html2canvas with minimal parsing - capture as-is
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        logging: false,
        height: pageHeightPx,
        width: pageWidthPx,
        useCORS: true,
        allowTaint: true,
        foreignObjectRendering: false,
        proxy: undefined,
        ignoreElements: (element) => {
          // Ignore style tags to prevent color parsing
          return element.tagName === "STYLE" || element.tagName === "SCRIPT";
        },
        onclone: (clonedDocument) => {
          // In the cloned document, remove all style elements
          clonedDocument.querySelectorAll("style").forEach((el) => {
            el.textContent = "";
          });
        },
      }).catch(() => {
        // If html2canvas fails with color parsing, use a simpler method
        console.warn("html2canvas failed, trying alternative method");
        throw new Error("Please ensure the chart is fully loaded");
      });

      console.log("Canvas generated successfully");

      // Convert to image
      const imgData = canvas.toDataURL("image/png");
      console.log("Image data generated");

      // Create PDF
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      const margin = 15;

      pdf.addImage(imgData, "PNG", margin, margin, imgWidth, imgHeight);
 
      const totalPages = pdf.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      addWatermark(i);
    }

      // Add additional pages if needed
      let heightLeft = imgHeight - (pageHeight - 20);
      let position = pageHeight - 10;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }


      // Save PDF
      const filename = `Chart_${new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(filename);
      console.log("PDF saved successfully");
    } catch (error) {
      console.error("PDF generation error:", error);

      // Fallback: Try using browser's native print-to-PDF
      try {
        console.log("Attempting fallback: Print to PDF");
        window.print();
      } catch (printError) {
        alert(
          `Error generating PDF: ${
            error instanceof Error ? error.message : "Unknown error"
          }`
        );
      }
    } finally {
      setDownloadingPdf(false);
    }
  };

const downloadWord = async () => {
  setDownloadingWord(true);
  try {
    if (!chartRef.current) {
      console.error("Chart reference is null");
      return;
    }
      // Helper function to convert base64 to Uint8Array
    const base64ToUint8Array = (base64: string): Uint8Array => {
      const binaryString = window.atob(base64);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes;
    };

    // Load logo image if logoUrl exists
    let logoImageData: { data: Uint8Array; mimeType: "jpg" | "png" | "gif" | "bmp" } | null = null;
    if (tenant?.logoUrl) {
      try {
        console.log('Loading logo from:', tenant.logoUrl);
        const response = await fetch(tenant.logoUrl);
        const blob = await response.blob();
        
        // Map MIME type to docx accepted format
        const getMimeType = (blobType: string): "jpg" | "png" | "gif" | "bmp" => {
          if (blobType.includes('png')) return 'png';
          if (blobType.includes('jpeg') || blobType.includes('jpg')) return 'jpg';
          if (blobType.includes('gif')) return 'gif';
          if (blobType.includes('bmp')) return 'bmp';
          return 'png';
        };
        
        const mimeType = getMimeType(blob.type);
        console.log('Detected MIME type:', mimeType);
        
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result as string;
            const base64Data = result.split(',')[1];
            resolve(base64Data);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        
        const uint8Array = base64ToUint8Array(base64);
        console.log('Logo loaded, size:', uint8Array.length, 'bytes');
        
        logoImageData = { data: uint8Array, mimeType };
      } catch (error) {
        console.error('Failed to load logo image:', error);
      }
    }

   const pageWidthPx = 750; // jsPDF default A4 size in pixels
      const pageHeightPx = 842;
    // Capture chart as image
    const canvas = await html2canvas(chartRef.current, {
      backgroundColor: "#ffffff",
      scale: 2,
      logging: false,
       height: pageHeightPx,
        width: pageWidthPx,
      useCORS: true,
      allowTaint: true,
      foreignObjectRendering: false,
      ignoreElements: (element) => {
        return element.tagName === "STYLE" || element.tagName === "SCRIPT";
      },
      onclone: (clonedDocument) => {
        clonedDocument.querySelectorAll("style").forEach((el) => {
          el.textContent = "";
        });
      },
    });

    console.log("Canvas generated:", canvas.width, "x", canvas.height);

    // Convert to base64
    const imgDataUrl = canvas.toDataURL("image/png");
    const base64String = imgDataUrl.split(",")[1];
    const imgBuffer = Uint8Array.from(atob(base64String), (c) =>
      c.charCodeAt(0)
    );

    // Import docx modules
    const {
      Document,
      Paragraph,
      ImageRun,
      Packer,
      convertInchesToTwip,
      AlignmentType,
      TextRun,
      Footer,
      HeadingLevel,
    } = await import("docx");

    // Calculate dimensions
    const pageWidth = convertInchesToTwip(8.5); // Letter page width
    const marginLeft = convertInchesToTwip(0.5);
    const marginRight = convertInchesToTwip(0.5);
    const contentWidth = pageWidth - marginLeft - marginRight;

    // Image width calculation
    const imageWidthInches = (contentWidth / 1440) * 0.9; // 90% of content width
    const canvasAspectRatio = canvas.height / canvas.width;
    const imageHeightInches = imageWidthInches * canvasAspectRatio;


     const footerChildren= [];
        
        if (logoImageData) {
          footerChildren.push(
            new Paragraph({
              children: [
                new ImageRun({
                  type: logoImageData.mimeType,
                  data: logoImageData.data,
                  transformation: {
                    width: 60,
                    height: 60,
                  },
                }),
              ],
              alignment: AlignmentType.RIGHT,
              spacing: { before: 100 },
            })
          );
        }
    const doc = new Document({
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: convertInchesToTwip(0.75),
                bottom: convertInchesToTwip(0.75),
                left: marginLeft,
                right: marginRight,
              },
              size: {
                width: pageWidth,
                height: convertInchesToTwip(11),
              },
            },
          },
           footers: {
                      default: new Footer({
                        children: footerChildren,
                      }),
                    },
          children: [
            // Title
            new Paragraph({
              text: "Growth Opportunities Report",
              heading: HeadingLevel.TITLE,
              alignment: AlignmentType.CENTER,
              spacing: { after: 300 },
            }),

            // Date
            new Paragraph({
              children: [
                new TextRun({
                  text: `Generated on ${new Date().toLocaleString()}`,
                  italics: true,
                  size: 20,
                  color: "666666",
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 },
            }),

            // Chart image - full width
            new Paragraph({
              children: [
                new ImageRun({
                  data: imgBuffer,
                  type: "png",
                  transformation: {
                    width: Math.round(imageWidthInches * 96), // pixels
                    height: Math.round(imageHeightInches * 96), // pixels
                  },
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 },
            }),

            // Optional: Add a description or footer
            new Paragraph({
              children: [
                new TextRun({
                  text: "This report contains analysis of growth opportunities.",
                  italics: true,
                  size: 20,
                  color: "888888",
                }),
              ],
              alignment: AlignmentType.CENTER,
            }),
          ],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `Growth_Opportunities_${new Date().toISOString().slice(0, 10)}.docx`);
    console.log("Word document saved successfully");
  } catch (err) {
    console.error("Word generation failed:", err);
    alert(`Error generating document: ${err instanceof Error ? err.message : "Unknown error"}`);
  } finally {
    setDownloadingWord(false);
  }
};
  if (loading || chartData.length === 0) {
    return (
      <div className="bg-white rounded-[20px] shadow border border-gray-200 p-6 max-w-4xl mx-auto animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-2/3 mb-4"></div>

        <div className="text-sm text-gray-600 flex flex-wrap gap-10 mb-6">
          <div>
            <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
            <div className="h-5 bg-gray-300 rounded w-20"></div>
          </div>
          <div>
            <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
            <div className="h-5 bg-gray-300 rounded w-20"></div>
          </div>
          <div>
            <div className="h-4 bg-gray-200 rounded w-24 mb-1"></div>
            <div className="h-5 bg-gray-300 rounded w-16"></div>
          </div>
        </div>

        {/* Bar chart skeleton */}
        <div className="h-[350px] w-full bg-gray-100 rounded mb-6"></div>

        {/* Source tags */}
        <div className="flex gap-2 mt-6">
          <div className="h-6 bg-gray-200 rounded-full w-32"></div>
          <div className="h-6 bg-gray-200 rounded-full w-24"></div>
        </div>
      </div>
    );
  }

  return (
    <div className=" max-w-4xl m-auto">
      <div className="flex gap-2  justify-end">
        <button
          onClick={downloadPDF}
          disabled={downloadingPdf}
          className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          {downloadingPdf ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Generating...
            </>
          ) : (
            <>
              <RiDownloadLine className="w-4 h-4" /> PDF
            </>
          )}
        </button>

        <button
          onClick={downloadWord}
          disabled={downloadingWord}
          className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          {downloadingWord ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Generating...
            </>
          ) : (
            <>
              <RiDownloadLine className="w-4 h-4" />
              Word
            </>
          )}
        </button>
      </div>
      <div
        ref={chartRef}
        className="bg-white rounded-2xl shadow border border-gray-200 p-6 max-w-4xl mx-auto mt-10 2xl:mt-20  "
      >
        <h2 className="text-2xl capitalize text-gray-700 mb-3">
          {data?.content?.core_market} - Growth Opportunities
        </h2>

        <div className="text-sm text-gray-600 flex flex-wrap gap-10 mb-1">
          <div>
            <p>Current estimated value (worldwide)</p>
            <p className="text-black text-base mt-1">
              {" "}
              ${currentEstimateValue().toFixed(1)} Bn in {startYear}{" "}
            </p>
          </div>
          <div>
            <p>Projected value (worldwide)</p>
            <p className="text-black text-base mt-1">
              ${projectedValue().toFixed()} Bn in {endYear}
            </p>
          </div>
          <div>
            <p>Annual CAGR</p>
            <p className="text-black text-base mt-1">{cagrValue.toFixed(1)}%</p>
          </div>
          <div className=" w-full flex justify-end ">
            <div className="  items-center flex  gap-3">
              {/* End Year Box */}
              <div className="flex  items-center gap-1">
                <div className="w-6 h-6 bg-gray-300 rounded-md border border-gray-300" />
                <span className="text-sm text-gray-700 font-medium">
                  {endYear}
                </span>
              </div>
              {/* Start Year Box */}
              <div className="flex  items-center gap-1">
                <div className="w-6 h-6  bg-gray-800 rounded-md " />
                <span className="text-sm text-gray-700 font-medium">
                  {startYear}
                </span>
              </div>
            </div>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 10, bottom: 40 }}
            barCategoryGap={1}
          >
            <CartesianGrid
              vertical={false}
              strokeDasharray="3 3"
              stroke="#E5E7EB"
            />
            <XAxis
              dataKey="region"
              tick={{ fontSize: 12, fill: "#4B5563" }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis hide />
            <Tooltip
              content={({ active, payload, label }: any) => {
                if (active && payload?.length) {
                  const regionData = payload[0]?.payload;
                  return (
                    <div className="bg-white border border-gray-200 rounded px-3 py-2 shadow">
                      <p className="font-semibold text-sm">{label}</p>
                      <p className="text-sm text-gray-700">
                        {endYear}:{" "}
                        <span className="text-gray-900 font-medium">
                          ${regionData?.total2033} Bn
                        </span>
                      </p>
                      <p className="text-sm text-gray-700">
                        {startYear}:{" "}
                        <span className="text-gray-900 font-medium">
                          ${regionData?.value2025} Bn
                        </span>
                      </p>
                    </div>
                  );
                }
                return null;
              }}
              cursor={{ fill: "transparent" }}
            />

            <Bar dataKey="value2025" stackId="a" radius={[0, 0, 0, 0]}>
              {chartData.map((_, index) => (
                <Cell
                  key={`2025-${index}`}
                  fill={colors2025[index % colors2025.length]}
                />
              ))}
              <LabelList
                dataKey="value2025"
                position="insideTop"
                content={({ x, y, value }: any) =>
                  value ? (
                    <text x={x + 10} y={y + 10} fill="#000" fontSize={12}>
                      ${value} Bn
                    </text>
                  ) : null
                }
              />
            </Bar>
            <Bar
              dataKey="growth"
              stackId="a"
              barSize={160}
              radius={[2, 2, 0, 0]}
            >
              {chartData.map((_, index) => (
                <Cell
                  key={`growth-${index}`}
                  fill={colors2033[index % colors2033.length]}
                />
              ))}

              <LabelList
                dataKey="total2033"
                position="top"
                content={({ x, y, value }: any) =>
                  value ? (
                    <text x={x + 10} y={y + 0} fill="#000" fontSize={12}>
                      ${value} Bn
                    </text>
                  ) : null
                }
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        <div className="mt-6 text-sm text-gray-500">
          {Array.isArray(data?.content?.market_size_current?.sources) &&
            data.content.market_size_current.sources.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-700">
                <span className="font-medium">Source(s):</span>
                {data.content.market_size_current.sources.map(
                  (source, idx) => {
                    let host = source;

                    try {
                      const url = new URL(source.trim());
                      host = url.hostname.replace("www.", "");
                    } catch {
                      // fallback if invalid URL
                      host = source.trim();
                    }

                    return (
                      <button
                        key={`${host}-${idx}`}
                        onClick={(e) => handleSourceClick(e, source)}
                        className="bg-white px-2 py-0.5 rounded-full border border-gray-200 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer"
                      >
                        {host}
                      </button>
                    );
                  }
                )}
              </div>
            )}
        </div>
        {iframeUrl && (
          <IframeViewer url={iframeUrl} onClose={() => setIframeUrl(null)} />
        )}
      </div>
    </div>
  );
};

export default GrowthOpportunities;
